// components/dashboard/widgets/TaxaConclusao.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, Progress, List, Tag } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';
import {
  TrophyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useDashboardData } from '../../../hooks/useDashboardData';

const TaxaConclusao = ({ endpoint, filtros, formato }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando taxa de conclusão...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '6px',
          color: '#a8071a'
        }}
      >
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar taxa de conclusão
        </p>
        <small style={{ color: '#cf1322' }}>{error?.message || String(error)}</small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📈</div>
        <h4 style={{ color: '#8c8c8c', margin: 0 }}>Nenhum dado de conclusão disponível</h4>
        <p style={{ color: '#8c8c8c', margin: '8px 0 0 0' }}>Aguardando dados dos cursos.</p>
      </div>
    );
  }

  // calcular taxa de conclusão
  const calcularTaxaConclusao = (item) => {
    const iniciados =
      parseInt(item.total_iniciados) ||
      parseInt(item.projetos_iniciados) ||
      parseInt(item.estudantes_matriculados) ||
      0;
    const concluidos =
      parseInt(item.total_concluidos) ||
      parseInt(item.projetos_concluidos) ||
      parseInt(item.defesas_aprovadas) ||
      0;

    return iniciados > 0 ? (concluidos / iniciados) * 100 : 0;
  };

  const getPerformanceCurso = (taxa) => {
    if (taxa >= 85) return 'excelente';
    if (taxa >= 70) return 'bom';
    if (taxa >= 55) return 'regular';
    if (taxa >= 40) return 'baixo';
    return 'critico';
  };

  // processar dados
  const dadosProcessados = dados
    .map((item) => {
      const curso =
        item.curso || item.nome_curso || item.area_especializacao || 'Curso não informado';
      const cursoAbrev = curso.length > 20 ? curso.substring(0, 20) + '...' : curso;
      const taxaConclusao = item.taxa_conclusao
        ? parseFloat(item.taxa_conclusao)
        : calcularTaxaConclusao(item);

      return {
        curso,
        curso_abrev: cursoAbrev,
        total_iniciados:
          parseInt(item.total_iniciados) ||
          parseInt(item.projetos_iniciados) ||
          parseInt(item.estudantes_matriculados) ||
          0,
        total_concluidos:
          parseInt(item.total_concluidos) ||
          parseInt(item.projetos_concluidos) ||
          parseInt(item.defesas_aprovadas) ||
          0,
        total_em_andamento:
          parseInt(item.total_em_andamento) || parseInt(item.em_desenvolvimento) || 0,
        total_abandonados: parseInt(item.total_abandonados) || parseInt(item.desistencias) || 0,
        taxa_conclusao: taxaConclusao,
        performance: getPerformanceCurso(taxaConclusao),
        tempo_medio_conclusao:
          parseInt(item.tempo_medio_conclusao) || parseInt(item.tempo_medio_dias) || 0,
        nota_media: parseFloat(item.nota_media) || parseFloat(item.media_notas) || 0,
        ano_referencia: item.ano_referencia || item.ano || new Date().getFullYear()
      };
    })
    .sort((a, b) => b.taxa_conclusao - a.taxa_conclusao);

  const getCor = (performance) => {
    const cores = {
      excelente: '#52c41a',
      bom: '#1890ff',
      regular: '#fadb14',
      baixo: '#fa8c16',
      critico: '#f5222d'
    };
    return cores[performance] || '#8c8c8c';
  };

  const getIcone = (performance) => {
    if (performance === 'excelente') return <TrophyOutlined />;
    if (performance === 'bom') return <CheckCircleOutlined />;
    if (performance === 'regular') return <BookOutlined />;
    if (performance === 'baixo') return <ClockCircleOutlined />;
    return <ExclamationCircleOutlined />;
  };

  const estatisticas = {
    totalCursos: dadosProcessados.length,
    taxaMedia:
      dadosProcessados.length > 0
        ? dadosProcessados.reduce((sum, i) => sum + i.taxa_conclusao, 0) /
          dadosProcessados.length
        : 0,
    cursosExcelentes: dadosProcessados.filter((i) => i.performance === 'excelente').length,
    totalIniciados: dadosProcessados.reduce((sum, i) => sum + i.total_iniciados, 0),
    totalConcluidos: dadosProcessados.reduce((sum, i) => sum + i.total_concluidos, 0),
    melhorCurso: dadosProcessados.length > 0 ? dadosProcessados[0] : null,
    piorCurso: dadosProcessados.length > 0 ? dadosProcessados[dadosProcessados.length - 1] : null
  };

  // --- Tooltip e seus PropTypes (corrige os avisos do eslint) ----------------
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      const taxa = Number(data.taxa_conclusao || 0);
      const nota = Number(data.nota_media || 0);

      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '250px'
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>📚 {data.curso}</p>
          <div style={{ fontSize: 12 }}>
            <p style={{ margin: '4px 0', color: '#1890ff' }}>
              📊 Taxa: <strong>{taxa.toFixed(1)}%</strong>
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>🎯 Iniciados: {data.total_iniciados}</p>
            <p style={{ margin: '4px 0', color: '#52c41a' }}>
              ✅ Concluídos: {data.total_concluidos}
            </p>
            {data.total_em_andamento > 0 && (
              <p style={{ margin: '4px 0', color: '#fa8c16' }}>
                🔄 Em andamento: {data.total_em_andamento}
              </p>
            )}
            {data.total_abandonados > 0 && (
              <p style={{ margin: '4px 0', color: '#f5222d' }}>
                ❌ Abandonados: {data.total_abandonados}
              </p>
            )}
            {data.tempo_medio_conclusao > 0 && (
              <p style={{ margin: '4px 0', color: '#666' }}>
                ⏰ Tempo médio: {data.tempo_medio_conclusao} dias
              </p>
            )}
            {nota > 0 && (
              <p style={{ margin: '4px 0', color: '#666' }}>⭐ Nota média: {nota.toFixed(1)}</p>
            )}
            <div
              style={{
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: `${getCor(data.performance)}20`,
                color: getCor(data.performance),
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 'bold'
              }}
            >
              {String(data.performance).toUpperCase()}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    label: PropTypes.any,
    payload: PropTypes.arrayOf(
      PropTypes.shape({
        payload: PropTypes.shape({
          curso: PropTypes.string,
          curso_abrev: PropTypes.string,
          total_iniciados: PropTypes.number,
          total_concluidos: PropTypes.number,
          total_em_andamento: PropTypes.number,
          total_abandonados: PropTypes.number,
          taxa_conclusao: PropTypes.number,
          performance: PropTypes.string,
          tempo_medio_conclusao: PropTypes.number,
          nota_media: PropTypes.number,
          ano_referencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      })
    )
  };

  // ------------------------------ LISTA --------------------------------------
  if (formato === 'lista') {
    return (
      <div style={{ height: 350, overflowY: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 8,
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 11
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.totalCursos}</div>
            <div style={{ color: '#666' }}>Cursos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {estatisticas.taxaMedia.toFixed(1)}%
            </div>
            <div style={{ color: '#666' }}>Taxa Média</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
              {estatisticas.cursosExcelentes}
            </div>
            <div style={{ color: '#666' }}>Excelentes</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#722ed1' }}>
              {estatisticas.totalConcluidos}
            </div>
            <div style={{ color: '#666' }}>Concluídos</div>
          </div>
        </div>

        <List
          dataSource={dadosProcessados}
          renderItem={(item, index) => (
            <List.Item
              style={{
                padding: '12px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: item.performance === 'excelente' ? '#f6ffed' : 'transparent'
              }}
            >
              <List.Item.Meta
                avatar={
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: getCor(item.performance),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 16
                    }}
                  >
                    {getIcone(item.performance)}
                  </div>
                }
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', fontSize: 13 }}>{item.curso}</span>
                    <Tag
                      color={getCor(item.performance)}
                      style={{ fontSize: 10, fontWeight: 'bold' }}
                    >
                      {item.taxa_conclusao.toFixed(1)}%
                    </Tag>
                    <Tag color="default" style={{ fontSize: 10 }}>
                      {item.performance.toUpperCase()}
                    </Tag>
                  </div>
                }
                description={
                  <div style={{ fontSize: 12 }}>
                    <div style={{ marginBottom: 8 }}>
                      <Progress
                        percent={item.taxa_conclusao}
                        strokeColor={getCor(item.performance)}
                        size="small"
                        format={(p) => `${p.toFixed(1)}%`}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: 16,
                        fontSize: 11,
                        color: '#666',
                        flexWrap: 'wrap'
                      }}
                    >
                      <span>🎯 Iniciados: {item.total_iniciados}</span>
                      <span>✅ Concluídos: {item.total_concluidos}</span>
                      {item.total_em_andamento > 0 && (
                        <span>🔄 Andamento: {item.total_em_andamento}</span>
                      )}
                      {item.total_abandonados > 0 && (
                        <span>❌ Abandonos: {item.total_abandonados}</span>
                      )}
                    </div>
                  </div>
                }
              />
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>#{index + 1}º lugar</div>
                {item.nota_media > 0 && (
                  <div style={{ fontSize: 11, color: '#666' }}>
                    Nota: {item.nota_media.toFixed(1)}
                  </div>
                )}
              </div>
            </List.Item>
          )}
        />
      </div>
    );
  }

  // ------------------------------- PIZZA -------------------------------------
  if (formato === 'pizza') {
    const dadosPizza = [
      { name: 'Excelente', value: estatisticas.cursosExcelentes, color: '#52c41a' },
      { name: 'Bom', value: dadosProcessados.filter((i) => i.performance === 'bom').length, color: '#1890ff' },
      { name: 'Regular', value: dadosProcessados.filter((i) => i.performance === 'regular').length, color: '#fadb14' },
      { name: 'Baixo', value: dadosProcessados.filter((i) => i.performance === 'baixo').length, color: '#fa8c16' },
      { name: 'Crítico', value: dadosProcessados.filter((i) => i.performance === 'critico').length, color: '#f5222d' }
    ].filter((i) => i.value > 0);

    return (
      <div style={{ height: 350 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: 16,
            padding: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            fontSize: 12
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.totalCursos}</div>
            <div style={{ color: '#666' }}>Total Cursos</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {estatisticas.taxaMedia.toFixed(1)}%
            </div>
            <div style={{ color: '#666' }}>Taxa Média</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
              {estatisticas.melhorCurso ? estatisticas.melhorCurso.taxa_conclusao.toFixed(1) : 0}%
            </div>
            <div style={{ color: '#666' }}>Melhor Taxa</div>
          </div>
        </div>

        <div style={{ width: '100%', height: 'calc(350px - 80px)' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={dadosPizza} cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2} dataKey="value">
                {dadosPizza.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, `${name} (${value} cursos)`]} />
              <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // ------------------------------- GRÁFICO -----------------------------------
  return (
    <div style={{ height: 350 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: 16,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          fontSize: 12
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{estatisticas.totalCursos}</div>
          <div style={{ color: '#666' }}>Cursos</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
            {estatisticas.taxaMedia.toFixed(1)}%
          </div>
          <div style={{ color: '#666' }}>Taxa Média</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>{estatisticas.cursosExcelentes}</div>
          <div style={{ color: '#666' }}>Excelentes</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#722ed1' }}>
            {estatisticas.totalIniciados > 0
              ? ((estatisticas.totalConcluidos / estatisticas.totalIniciados) * 100).toFixed(1)
              : 0}
            %
          </div>
          <div style={{ color: '#666' }}>Taxa Geral</div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(350px - 110px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dadosProcessados} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="curso_abrev"
              tick={{ fontSize: 10 }}
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              label={{ value: 'Taxa de Conclusão (%)', angle: -90, position: 'insideLeft' }}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="taxa_conclusao" name="Taxa de Conclusão (%)" radius={[4, 4, 0, 0]}>
              {dadosProcessados.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getCor(entry.performance)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#52c41a' }} />
          Excelente (≥85%)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1890ff' }} />
          Bom (70-84%)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fadb14' }} />
          Regular (55-69%)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          Crítico (&lt;40%)
        </span>
      </div>
    </div>
  );
};

TaxaConclusao.displayName = 'TaxaConclusao';
TaxaConclusao.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  formato: PropTypes.oneOf(['grafico', 'lista', 'pizza'])
};

TaxaConclusao.defaultProps = {
  filtros: {},
  formato: 'grafico'
};

export default TaxaConclusao;
